// StudentData.js
 import React, { useEffect, useState } from "react";
import { collection, query, getDocs } from "firebase/firestore";
import { auth, db } from "./firebase";
import styles from "./MySkillsTab.module.css";



const sections = [
  {
    title: "Preliminary Testing & History",
    skills: ["Chief Complaint (CC) & History of Present Illness (HPI)", "Medical, Ocular, and Family History", "Visual Acuity", "Pupillary Testing", "Extraocular Motilities (EOMs)", "Cover Test", "Confrontation Visual Fields", "Color Vision & Stereopsis", "Lensometry", "Autorefraction / Keratometry", "Intraocular Pressure (Tonometry)"],
  },
  {
    title: "Refraction",
    skills: ["Retinoscopy", "Autorefraction", "Subjective Refraction", "Binocular Balance"],
  },
  {
    title: "Ocular Health Evaluation",
    skills: ["External Exam (lids, lashes, adnexa)", "Slit Lamp Exam (cornea, anterior chamber, iris, lens)", "Fundus Exam (optic nerve, retina, macula)—often done with dilation", "Gonioscopy (if glaucoma suspected)"],
  },
  {
    title: "Special Testing",
    skills: ["Optical Coherence Tomography (OCT)", "Visual Fields", "Tear Film Testing (TBUT, Schirmer’s)", "Corneal Topography / Pachymetry", "Fundus Photography", "Additional binocular vision or accommodative tests", "Others as needed based on findings"],
  },
  {
    title: "Assessment, Plan & Patient Education",
    skills: ["Final Diagnosis and Management Plan", "Prescribe glasses/contact lenses", "Recommend medications or therapy", "Patient education (ocular health, treatment plan, follow-up)", "Referral to a specialist if needed"],
  },
];

const uncommonSkills = {
  "Preliminary Testing & History": [
    "Near Point of Convergence (NPC)", "Vergence Ranges (Base-in/Base-out ranges)", "Accommodative Amplitude, Facility, and Lag testing", "Blood Pressure measurement", "Pulse Oximetry", "Amsler Grid (macular function screening)", "Red Cap Desaturation (optic nerve function)", "Maddox Rod / Maddox Wing tests", "Heterophoria Testing (von Graefe, Howell Card)", "Fixation Disparity Testing (Wesson Card, Saladin Card)", "Dark Adaptation Screening (basic handheld tests)", "Questionnaires: OSDI, CISS, NEI VFQ-25",
  ],
  "Refraction": [
    "Cycloplegic Refraction", "Near Refraction", "Refraction for Irregular Corneas", "Stenopaic Slit Refraction", "Radical Retinoscopy", "Over-plus and Fogging Techniques", "Duochrome Test", "MEM Retinoscopy",
  ],
  "Ocular Health Evaluation": [
    "Corneal Staining", "Tear Break-Up Time (TBUT)", "Schirmer’s Test", "Lid Eversion & Meibomian Gland Expression", "Seidel Test", "Corneal Sensitivity Testing", "Specular Microscopy", "Scleral Depression", "Anterior Segment Photography", "Lid Margin and Lash Debris Evaluation",
  ],
  "Special Testing (If Indicated)": [
    "B-scan Ultrasonography", "Electrodiagnostics (ERG, VEP, EOG)", "Anterior Segment OCT", "Fundus Autofluorescence", "Confocal Microscopy", "Tear Film Interferometry", "Tear Osmolarity Testing", "InflammaDry MMP-9 Testing", "Macular Pigment Optical Density Testing", "Microperimetry", "Multifocal ERG / Pattern ERG", "Contrast Sensitivity Testing", "Dark Adaptometry",
  ],
  "Assessment, Plan & Patient Education": [
    "Specialty Contact Lens Fitting", "Myopia Control Treatments", "Low Vision Rehabilitation", "Vision Therapy Prescription", "Sports Vision Training", "Nutritional Counseling", "Safety & Occupational Lens Counseling", "Driving Restriction Counseling", "Minor In-Office Procedures", "Minor Laceration Repair",
  ],
};


function MySkillsTab() {
  const [skillTallies, setSkillTallies] = useState({});
  const [visitTallies, setVisitTallies] = useState({});
  const [groupedSkills, setGroupedSkills] = useState({});
  const [cptTallies, setCptTallies] = useState({});
  const [icdTallies, setIcdTallies] = useState({});
  const getSkillLevel = (count) => {
  if (count >= 125) return "🧙 Expert";
  if (count >= 50) return "🧑‍🎓 Intermediate";
  return "👶 Beginner";
};



  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const userEmail = auth.currentUser.email;
        const logsRef = collection(db, "users", userEmail, "logs");
        const q = query(logsRef);
        const querySnapshot = await getDocs(q);

        console.log("Total logs fetched:", querySnapshot.size);
        const skillsCount = {};
        const visitTypeCount = {};
        const cptCount = {};
        const icdCount = {};


        querySnapshot.forEach((doc) => {
  const data = doc.data();
  console.log("Log data:", data);

  // Tally skills
  if (Array.isArray(data.skills)) {
    data.skills.forEach((skill) => {
      skillsCount[skill] = (skillsCount[skill] || 0) + 1;
    });
  }

  // Tally visit types
  if (data.visitType) {
    const type = data.visitType.trim();
    visitTypeCount[type] = (visitTypeCount[type] || 0) + 1;
  }

  // ✅ Tally CPT codes
  if (Array.isArray(data.cptCodes)) {
    data.cptCodes.forEach((code) => {
      cptCount[code] = (cptCount[code] || 0) + 1;
    });
  }

  // ✅ Tally ICD-10 codes
  if (Array.isArray(data.diagnoses)) {
    data.diagnoses.forEach((code) => {
      icdCount[code] = (icdCount[code] || 0) + 1;
    });
  }
});


        setSkillTallies(skillsCount);
        // === Organize skills into sections ===
const mergedSkillSections = {};

// Add common skills
sections.forEach(section => {
  section.skills.forEach(skill => {
    mergedSkillSections[skill] = section.title;
  });
});

// Add uncommon skills (normalize section titles)
Object.entries(uncommonSkills).forEach(([section, skills]) => {
  const normalized = section.replace(" (If Indicated)", "");
  skills.forEach(skill => {
    mergedSkillSections[skill] = normalized;
  });
});

// Group skill tallies by section
const groupedBySection = {};

Object.entries(skillsCount).forEach(([skill, count]) => {
  const section = mergedSkillSections[skill] || "Uncategorized";

  if (!groupedBySection[section]) {
    groupedBySection[section] = {};
  }

  groupedBySection[section][skill] = count;
});

setGroupedSkills(groupedBySection);

        setVisitTallies(visitTypeCount);
        setCptTallies(cptCount);
        setIcdTallies(icdCount);
      } catch (error) {
        console.error("🔥 Error fetching logs:", error);
      }
    };

    fetchLogs();
  }, []);

  return (
    <div className={styles.mySkillsBox}>
      <h2>🧠 My Skills</h2>
      <div
  style={{
    background: "#fffbe6", // soft yellow background
    padding: "16px",
    borderRadius: "10px",
    border: "1px solid #ffe58f",
    marginTop: "2rem",
    marginBottom: "2rem" // <-- Add this line for spacing below
  }}
>
  <p style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>
    Total Skills Logged:{" "}
    <strong>{Object.values(skillTallies).reduce((a, b) => a + b, 0)}</strong>
  </p>

  <h4 style={{ marginTop: "1rem" }}>Top 5 Most Performed Skills:</h4>
  <ul style={{ paddingLeft: "20px", margin: 0 }}>
    {Object.entries(skillTallies)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([skill, count]) => (
        <li key={skill} style={{ marginBottom: "4px" }}>
          {skill} — <strong>{count}</strong> time{count > 1 ? "s" : ""}
        </li>
      ))}
  </ul>
</div>



      {Object.keys(skillTallies).length === 0 && Object.keys(visitTallies).length === 0 ? (
        <p>No logs found yet.</p>
      ) : (
        <>
        <div style={{ marginTop: "2rem", marginBottom: "1.5rem" }}>
  <h3 style={{ marginBottom: "0.5rem" }}>🔑 Skill Level Key</h3>
  <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
    <span><strong>👶 Beginner:</strong> 0–49 times</span>
    <span><strong>🧑‍🎓 Intermediate:</strong> 50–124 times</span>
    <span><strong>🧙 Expert:</strong> 125+ times</span>
  </div>
</div>


          {Object.entries(groupedSkills).map(([section, skills]) => (
  <div key={section} className={styles.skillSection}>
    <h3 className={styles.skillSectionTitle}>{section}</h3>
    <div className={styles.skillGrid}>
      {Object.entries(skills).map(([skill, count]) => (
  <div key={skill} className={styles.skillItem}>
    <span className={styles.skillName}>{skill}</span>
    <span className={styles.skillCount}>
      {count}× <span style={{ fontWeight: "bold", marginLeft: "6px" }}>{getSkillLevel(count)}</span>
    </span>
  </div>
))}
    </div>
  </div>
))}


          {/* 📋 Visit Type Summary */}
<div style={{ background: "#e7fbe9", padding: "16px", borderRadius: "10px", marginTop: "2rem", border: "1px solid #b2dfdb" }}>
  <h3 style={{ marginBottom: "1rem" }}>📋 Visit Type Summary</h3>
  <ul>
    {Object.entries(visitTallies)
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => (
        <li key={type}>📁 {type} — {count} time{count > 1 ? "s" : ""}</li>
      ))}
  </ul>
</div>

{/* 🧾 CPT Code Tally */}
<div style={{ background: "#f1f9ff", padding: "16px", borderRadius: "10px", marginTop: "2rem", border: "1px solid #cce5ff" }}>
  <h3 style={{ marginBottom: "1rem" }}>🧾 CPT Code Tally</h3>
  <ul>
    {Object.entries(cptTallies)
      .sort((a, b) => b[1] - a[1])
      .map(([code, count], index) => (
        <li key={code}>
          {index < 3 ? "🏅 " : ""}{code} — {count} time{count > 1 ? "s" : ""}
        </li>
      ))}
  </ul>
</div>

{/* 🧠 ICD-10 Code Tally */}
<div style={{ background: "#fff9f2", padding: "16px", borderRadius: "10px", marginTop: "2rem", border: "1px solid #ffe0b2" }}>
  <h3 style={{ marginBottom: "1rem" }}>🧠 ICD-10 Code Tally</h3>
  <ul>
    {Object.entries(icdTallies)
      .sort((a, b) => b[1] - a[1])
      .map(([code, count], index) => (
        <li key={code}>
          {index < 3 ? "🏅 " : ""}{code} — {count} time{count > 1 ? "s" : ""}
        </li>
      ))}
  </ul>
</div>

</>
)}
</div>
);
}

export default MySkillsTab;
